// src/pages/CalculatorFunding.js
import React, { useState, useEffect } from "react";

const investorTypes = [
  "Angel",
  "VC",
  "VC Seed",
  "VC Series A",
  "VC Series B",
  "Private Equity",
  "Debt / Note",
  "Strategic",
  "Crowdfunding",
  "Friends & Family"
];

const instrumentTypes = [
  "Equity Shares",
  "Convertible Note",
  "SAFE",
  "Debt Financing",
  "ESOP Expansion"
];

const stageInvestorProfiles = {
  "Pre-MVP": [
    { type: "Angel", instrument: "Convertible Note", askAmount: 500000, askDilution: 5 },
    { type: "Friends & Family", instrument: "Equity Shares", askAmount: 200000, askDilution: 2 }
  ],
  "MVP": [
    { type: "Angel", instrument: "Equity Shares", askAmount: 1000000, askDilution: 10 },
    { type: "VC Seed", instrument: "SAFE", askAmount: 2000000, askDilution: 20 }
  ],
  "GTM": [
    { type: "VC Series A", instrument: "Equity Shares", askAmount: 5000000, askDilution: 25 },
    { type: "Strategic", instrument: "Convertible Note", askAmount: 2000000, askDilution: 10 }
  ]
  // … add others as needed
};

export default function CalculatorFunding({ 
  stage = "", 
  preMoneyValuation = 0, 
  ownerShares = 70, 
  esopShares = 10, 
  onSave, 
  onCancel 
}) {
  const [investors, setInvestors] = useState([]);
  const [parValue, setParValue] = useState(10);
  const [roundStart, setRoundStart] = useState("");
  const [roundEnd, setRoundEnd] = useState("");
  const [roundStatus, setRoundStatus] = useState("Open");

  useEffect(() => {
    if (stage) {
      // prefill achieved with ask
      const defaults = (stageInvestorProfiles[stage] || []).map(inv => ({
        ...inv,
        achievedAmount: inv.askAmount,
        achievedDilution: inv.askDilution
      }));
      setInvestors(defaults);
    }
  }, [stage]);

  const handleInvestorChange = (index, field, value) => {
    const updated = [...investors];
    updated[index][field] = Number(value) || value;
    setInvestors(updated);
  };

  const totalRaised = investors.reduce((sum, inv) => sum + (Number(inv.achievedAmount) || 0), 0);
  const postMoneyValuation = preMoneyValuation + totalRaised;
  const totalDilution = investors.reduce((sum, inv) => sum + (Number(inv.achievedDilution) || 0), 0);

  const ownerPercentAfter = ownerShares * (1 - totalDilution / 100);
  const esopPercentAfter = esopShares;

  const totalSharesBefore = preMoneyValuation / parValue;
  const newSharesIssued = totalRaised / parValue;
  const totalSharesAfter = totalSharesBefore + newSharesIssued;

  const handleSave = () => {
    if (typeof onSave === "function") {
      onSave({
        stage,
        investors,
        roundStart,
        roundEnd,
        roundStatus,
        totalRaised,
        preMoneyValuation,
        postMoneyValuation,
        totalDilution,
        ownerPercentAfter,
        esopPercentAfter,
        totalSharesAfter,
        parValue
      });
    }
  };

const addInvestor = () => {
  setInvestors([
    ...investors,
    { type: "", instrument: "", askAmount: 0, askDilution: 0, achievedAmount: 0, achievedDilution: 0 }
  ]);
};

const removeInvestor = (index) => {
  setInvestors(investors.filter((_, i) => i !== index));
};

  return (
    <div style={{
      position: "fixed", top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      background: "#fff", border: "1px solid #ccc",
      borderRadius: "8px", width: "1000px", maxHeight: "80vh",
      overflowY: "auto", zIndex: 10001, padding: "20px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
    }}>
      <h4>Funding Round & Cap Table</h4>
      <p><strong>Stage:</strong> {stage}</p>
      <p><strong>Pre-Money Valuation:</strong> ₹{preMoneyValuation.toLocaleString("en-IN")}</p>



<table className="w3-table w3-table-all" style={{ marginTop: "15px" }}>
      <tbody>
         <tr>
              <td>
      <label>Par Value per Share (₹)</label>
      <input
        type="number"
        value={parValue}
        onChange={(e) => setParValue(Number(e.target.value))}
        className="w3-input w3-border"
        style={{ marginBottom: "15px" }}
      />
      </td>
      <td>

      <label>Round Start</label>
      <input type="date" value={roundStart} onChange={(e) => setRoundStart(e.target.value)} className="w3-input w3-border" />

      </td>
      <td>
      <label>Round End</label>
      <input type="date" value={roundEnd} onChange={(e) => setRoundEnd(e.target.value)} className="w3-input w3-border" />
      
      </td>
      <td>
      <label>Status</label>
      <select value={roundStatus} onChange={(e) => setRoundStatus(e.target.value)} className="w3-select w3-border">
        <option value="Open">Open</option>
        <option value="Closed">Closed</option>
      </select>


      </td>
      </tr>

      </tbody>
      </table>
      <table className="w3-table w3-table-all" style={{ marginTop: "15px" }}>
        <thead>
          <tr>
            <th>Investor Type</th>
            <th>Instrument</th>
            <th>Ask</th>
            <th style={{width: "10%"}}>For %</th>
            <th >Achieved</th>
            <th style={{width: "10%"}}>For %</th>
          </tr>
        </thead>

        <tbody>
  {investors.map((inv, i) => (
    <tr key={i}>
      <td>
        <select className="w3-input w3-border" value={inv.type} onChange={(e) => handleInvestorChange(i, "type", e.target.value)}>
          <option value="">-- Select Investor --</option>
          {investorTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td>
        <select className="w3-input w3-border" value={inv.instrument} onChange={(e) => handleInvestorChange(i, "instrument", e.target.value)}>
          <option value="">-- Select Instrument --</option>
          {instrumentTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td>
        <input className="w3-input w3-border" type="number" value={inv.askAmount || 0} onChange={(e) => handleInvestorChange(i, "askAmount", e.target.value)} />
      </td>
      <td>
        <input className="w3-input w3-border" type="number" value={inv.askDilution || 0} onChange={(e) => handleInvestorChange(i, "askDilution", e.target.value)} />
      </td>
      <td>
        <input className="w3-input w3-border" type="number" value={inv.achievedAmount || 0} onChange={(e) => handleInvestorChange(i, "achievedAmount", e.target.value)} />
      </td>
      <td>
        <input className="w3-input w3-border" type="number" value={inv.achievedDilution || inv.askDilution || 0} onChange={(e) => handleInvestorChange(i, "achievedDilution", e.target.value)} />
      </td>
      <td>
        <button className="w3-button w3-red" onClick={() => removeInvestor(i)}>✕</button>
      </td>
    </tr>
  ))}
</tbody>

      </table>
      <button
  className="w3-button w3-light-grey"
  style={{ marginTop: "10px" }}
  onClick={addInvestor}
>
  + Add Investor
</button>


      <h5 style={{ marginTop: "20px" }}>Cap Table Simulation</h5>
      <table className="w3-table-all">
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>% Before</th>
            <th>% After</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Owners</td><td>{ownerShares}%</td><td>{ownerPercentAfter.toFixed(2)}%</td></tr>
          <tr><td>ESOP Pool</td><td>{esopShares}%</td><td>{esopPercentAfter.toFixed(2)}%</td></tr>
          <tr><td>Investors (Total)</td><td>0%</td><td>{totalDilution.toFixed(2)}%</td></tr>
        </tbody>
      </table>

            <h5 style={{ marginTop: "20px" }}>Share Calculation</h5>
      <table className="w3-table-all">
        <tbody>
          <tr><td>Total Shares Before</td><td>{totalSharesBefore.toFixed(0)}</td></tr>
          <tr><td>New Shares Issued</td><td>{newSharesIssued.toFixed(0)}</td></tr>
          <tr><td>Total Shares After</td><td>{totalSharesAfter.toFixed(0)}</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: "15px" }}>
        <strong>Total Raised (Achieved): ₹{totalRaised.toLocaleString("en-IN")}</strong><br/>
        <strong>Post-Money Valuation: ₹{postMoneyValuation.toLocaleString("en-IN")}</strong><br/>
        <strong>Total Dilution % (Achieved): {totalDilution.toFixed(2)}%</strong><br/>
        <strong>Round Status: {roundStatus}</strong><br/>
        <strong>Round Period: {roundStart || "?"} → {roundEnd || "?"}</strong>
      </div>

      <div style={{ marginTop: "15px" }}>
        <button className="w3-button w3-teal" onClick={handleSave}>Save</button>
        <button
          className="w3-button w3-light-grey"
          style={{ marginLeft: "10px" }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
